<!--
  Copyright (C) 2022 - Jeci SARL - https://jeci.fr

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
-->
<template>
  <div class="array-editor">
    <VueDraggableNext
      v-model="localValue"
      ghost-class="ghost"
      handle=".drag-handle"
      item-key="index"
      @end="onDragEnd"
    >
      <div
        v-for="(item, index) in localValue"
        :key="index"
        class="array-item"
        :style="{ backgroundColor: getBackgroundColor(item) }"
      >
        <button
          class="drag-handle"
          :aria-label="$t('config.arrayEditor.reorder')"
        >
          <i class="ri-draggable" aria-hidden="true"></i>
        </button>
        <PrimeInputText
          v-if="isSimple(item)"
          :model-value="item"
          @update:model-value="updateItem(index, $event)"
          class="p-inputtext-sm"
        />
        <div v-else class="object-item">
          <div v-if="isMetadata">
            <!-- Edit Mode -->
            <div v-if="editingIndex === index">
              <div
                v-for="subKey in Object.keys(metadataTemplate)"
                :key="subKey"
                class="object-property"
              >
                <label>{{ subKey }}</label>
                <PrimeSelect
                  v-if="subKey === 'input'"
                  :model-value="item[subKey]"
                  @update:model-value="
                    updateObjectProperty(index, subKey, $event)
                  "
                  :options="inputOptions"
                  class="p-inputtext-sm"
                />
                <PrimeMultiSelect
                  v-else-if="subKey === 'display'"
                  :model-value="item[subKey]"
                  @update:model-value="
                    updateObjectProperty(index, subKey, $event)
                  "
                  :options="displayOptions"
                  class="p-inputtext-sm"
                />
                <PrimeToggleSwitch
                  v-else-if="subKey === 'readOnly'"
                  :model-value="item[subKey]"
                  @update:model-value="
                    updateObjectProperty(index, subKey, $event)
                  "
                />
                <PrimeInputText
                  v-else
                  :model-value="item[subKey]"
                  @update:model-value="
                    updateObjectProperty(index, subKey, $event)
                  "
                  class="p-inputtext-sm"
                />
              </div>
            </div>
            <!-- View Mode -->
            <div v-else class="object-summary">
              <div class="summary-item"><strong>ID:</strong> {{ item.id }}</div>
              <div class="summary-item" v-if="item.title">
                <strong>Title:</strong> {{ item.title }}
              </div>
              <div class="summary-item" v-if="item.labelId">
                <strong>Label ID:</strong> {{ item.labelId }}
              </div>
              <div class="summary-item">
                <strong>Input:</strong> {{ item.input }}
              </div>
            </div>
          </div>
          <div v-else>
            <div
              v-for="(subValue, subKey) in item"
              :key="subKey"
              class="object-property"
            >
              <label>{{ subKey }}</label>
              <PrimeInputText
                :model-value="subValue"
                @update:model-value="
                  updateObjectProperty(index, subKey, $event)
                "
                class="p-inputtext-sm"
              />
            </div>
          </div>
        </div>
        <div class="action-buttons">
          <PrimeButton
            v-if="isMetadata && editingIndex !== index"
            icon="ri-pencil-line"
            :aria-label="$t('modify')"
            class="p-button-secondary p-button-sm"
            @click="editItem(index)"
          />
          <PrimeButton
            v-if="isMetadata && editingIndex === index"
            icon="ri-check-line"
            :aria-label="$t('save')"
            class="p-button-success p-button-sm"
            @click="saveItem()"
          />
          <PrimeButton
            v-if="isMetadata"
            icon="ri-arrow-up-line"
            :aria-label="$t('moveUp')"
            class="p-button-secondary p-button-sm"
            @click="moveItem(index, -1)"
            :disabled="index === 0"
          />
          <PrimeButton
            v-if="isMetadata"
            icon="ri-arrow-down-line"
            :aria-label="$t('moveDown')"
            class="p-button-secondary p-button-sm"
            @click="moveItem(index, 1)"
            :disabled="index === localValue.length - 1"
          />
          <PrimeButton
            v-if="isMetadata"
            icon="ri-file-copy-line"
            :aria-label="$t('duplicate')"
            class="p-button-secondary p-button-sm"
            @click="duplicateItem(index)"
          />
          <PrimeButton
            icon="ri-delete-bin-line"
            :aria-label="$t('delete')"
            class="p-button-danger p-button-sm"
            @click="removeItem(index)"
          />
        </div>
      </div>
    </VueDraggableNext>
    <PrimeButton
      :label="$t('config.arrayEditor.addItem')"
      icon="ri-add-line"
      class="p-button-sm p-button-secondary mt-2"
      @click="addItem"
    />
  </div>
</template>

<script setup>
import { ref, watch } from "vue";
import { VueDraggableNext } from "vue-draggable-next";
import PrimeInputText from "primevue/inputtext";
import PrimeButton from "primevue/button";
import PrimeSelect from "primevue/select";
import PrimeMultiSelect from "primevue/multiselect";
import PrimeToggleSwitch from "primevue/toggleswitch";

const props = defineProps({
  modelValue: {
    type: Array,
    required: true,
  },
  isMetadata: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["update:modelValue"]);

const localValue = ref(JSON.parse(JSON.stringify(props.modelValue)));
const inputOptions = ["text", "datetime", "boolean", "double", "int", "date"];
const displayOptions = ["popup", "details", "advancedSearch", "view"];
const editingIndex = ref(null);

const metadataTemplate = {
  id: "",
  title: "",
  labelId: "",
  display: [],
  input: "text",
  readOnly: false,
  modelAspect: "",
  modelType: "",
};

watch(
  () => props.modelValue,
  (newValue) => {
    localValue.value = JSON.parse(JSON.stringify(newValue));
  },
  { deep: true },
);

const isSimple = (item) => {
  const type = typeof item;
  return type === "string" || type === "number" || type === "boolean";
};

const isObjectOrArray = (item) => {
  return typeof item === "object" && item !== null;
};

const updateItem = (index, value) => {
  localValue.value[index] = value;
  emit("update:modelValue", localValue.value);
};

const updateObjectProperty = (itemIndex, key, value) => {
  localValue.value[itemIndex][key] = value;
  emit("update:modelValue", localValue.value);
};

const removeItem = (index) => {
  localValue.value.splice(index, 1);
  emit("update:modelValue", localValue.value);
};

const addItem = () => {
  if (props.isMetadata) {
    const newItem = {
      ...metadataTemplate,
      order: localValue.value.length + 1,
    };
    localValue.value.push(newItem);
  } else {
    if (localValue.value.length > 0) {
      const firstItem = localValue.value[0];
      const firstItemType = typeof firstItem;

      if (firstItemType === "string") {
        localValue.value.push("");
      } else if (firstItemType === "number") {
        localValue.value.push(0);
      } else if (firstItemType === "object" && firstItem !== null) {
        localValue.value.push(JSON.parse(JSON.stringify(firstItem)));
      } else {
        localValue.value.push(""); // Default
      }
    } else {
      localValue.value.push(""); // Default for an empty array
    }
  }
  emit("update:modelValue", localValue.value);
};

const editItem = (index) => {
  const item = localValue.value[index];
  localValue.value[index] = { ...metadataTemplate, ...item };
  editingIndex.value = index;
};

const saveItem = () => {
  editingIndex.value = null;
  emit("update:modelValue", localValue.value);
};

const duplicateItem = (index) => {
  const itemToDuplicate = JSON.parse(JSON.stringify(localValue.value[index]));
  localValue.value.splice(index + 1, 0, itemToDuplicate);

  // Update order property if it exists
  localValue.value.forEach((item, i) => {
    if (
      isObjectOrArray(item) &&
      Object.prototype.hasOwnProperty.call(item, "order")
    ) {
      item.order = i + 1;
    }
  });

  emit("update:modelValue", localValue.value);
};

const moveItem = (index, direction) => {
  const newIndex = index + direction;
  if (newIndex < 0 || newIndex >= localValue.value.length) {
    return;
  }

  const item = localValue.value.splice(index, 1)[0];
  localValue.value.splice(newIndex, 0, item);

  localValue.value.forEach((item, i) => {
    if (
      isObjectOrArray(item) &&
      Object.prototype.hasOwnProperty.call(item, "order")
    ) {
      item.order = i + 1;
    }
  });

  emit("update:modelValue", localValue.value);
};

const onDragEnd = () => {
  // Update order property for items that have it
  localValue.value.forEach((item, i) => {
    if (
      isObjectOrArray(item) &&
      Object.prototype.hasOwnProperty.call(item, "order")
    ) {
      item.order = i + 1;
    }
  });
  emit("update:modelValue", localValue.value);
};

const getBackgroundColor = (item) => {
  if (!props.isMetadata) {
    return "transparent";
  }
  const str = item.modelAspect || item.modelType;
  if (!str) {
    return "transparent";
  }

  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }

  const h = hash % 360;
  return `hsla(${h}, 50%, 50%, 0.15)`;
};
</script>

<style scoped>
.array-editor {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
.array-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.array-item .p-inputtext-sm {
  flex-grow: 1;
}

.object-item {
  border: 1px solid var(--p-surface-border);
  border-radius: 4px;
  padding: 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: 100%;
}

.object-property {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.object-property label {
  font-weight: bold;
  width: 120px;
  text-align: right;
  font-size: 0.875rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.object-summary {
  display: flex;
  gap: 1rem;
  align-items: center;
  width: 100%;
}

.summary-item {
  font-size: 0.875rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.drag-handle {
  cursor: grab;
  display: flex;
  align-items: center;
  padding: 0.25rem;
  background: none;
  border: none;
  color: var(--p-text-muted-color);
  font-size: 1.25rem;
  flex-shrink: 0;
}

.drag-handle:active {
  cursor: grabbing;
}

.ghost {
  opacity: 0.4;
}

.action-buttons {
  display: flex;
  gap: 0.5rem;
  visibility: hidden;
}

.array-item:hover .action-buttons {
  visibility: visible;
}
</style>
